import { STUDENT_BODY_PHOTO_POSITIONS, getStudentBodyPhotoAssessmentDate } from '../services/studentPhotoStorage';
import type { PhotoPosition } from '../types';

export interface BodyPhotoAssessmentLike {
  assessmentDate: string;
  photos: Array<{ position: string }>;
}

export const getCurrentStudentBodyPhotoYearMonth = (referenceDate: Date = new Date()): string => (
  getStudentBodyPhotoAssessmentDate(referenceDate).slice(0, 7)
);

export const hasCompleteBodyPhotoSet = (positions: Iterable<string>): boolean => {
  const normalized = new Set(
    Array.from(positions)
      .map((position) => position.trim())
      .filter((position): position is PhotoPosition => (
        STUDENT_BODY_PHOTO_POSITIONS.includes(position as PhotoPosition)
      )),
  );

  return STUDENT_BODY_PHOTO_POSITIONS.every((position) => normalized.has(position));
};

/** True when a full body-photo set already exists for the current BR calendar month (or complete legacy undated set). */
export const hasCompleteBodyPhotosForCurrentPeriod = (
  assessments: BodyPhotoAssessmentLike[],
  referenceDate: Date = new Date(),
  options?: { cycleStartedOn?: string | null },
): boolean => {
  const cycleStartedOn = options?.cycleStartedOn?.trim() || null;
  if (cycleStartedOn) {
    const cyclePositions = assessments
      .filter((assessment) => (
        assessment.assessmentDate !== 'legacy'
        && assessment.assessmentDate >= cycleStartedOn
      ))
      .flatMap((assessment) => assessment.photos.map((photo) => photo.position));

    return hasCompleteBodyPhotoSet(cyclePositions);
  }

  const yearMonth = getCurrentStudentBodyPhotoYearMonth(referenceDate);
  const periodPositions = assessments
    .filter((assessment) => (
      assessment.assessmentDate !== 'legacy'
      && assessment.assessmentDate.startsWith(yearMonth)
    ))
    .flatMap((assessment) => assessment.photos.map((photo) => photo.position));

  if (hasCompleteBodyPhotoSet(periodPositions)) {
    return true;
  }

  const legacyPositions = assessments
    .filter((assessment) => assessment.assessmentDate === 'legacy')
    .flatMap((assessment) => assessment.photos.map((photo) => photo.position));

  return hasCompleteBodyPhotoSet(legacyPositions);
};
